const post = {
  slug: 'gcp-iam-and-service-accounts-explained-plainly',
  number: 24,
  title: 'GCP IAM and Service Accounts, Explained Plainly',
  date: '2025-11-03',
  readTime: '6 min read',
  views: 30,
  category: 'gcp',
  tags: ['GCP', 'IAM', 'Security'],
  excerpt:
    'Permissions are where most people get stuck on Google Cloud. The model is actually clean once you see its three pieces and the one habit that keeps it safe.',
  cover: null,
  content: `
Permissions are where most people get stuck on Google Cloud, and it is almost always because they are trying to memorize specifics before they understand the shape. I did the same thing at first, collecting role names and copying snippets without a model to hang them on, and it felt impenetrable. Then someone laid out the model in three pieces, and the fog lifted in about ten minutes. The GCP permission system is genuinely clean once you see it, and most of staying safe comes down to a single habit. Let me hand you both.

## Three pieces: who, what, and where

Every permission decision on GCP is an answer to three questions: who is asking, what are they allowed to do, and where does that permission apply.

The who is a member, which might be a person or, very often, a service account that represents an application. The what is a role, which is a bundle of specific permissions grouped together. The where is the resource, the project or bucket or dataset the permission is scoped to.

\`\`\`text
grant  [role]  to  [member]  on  [resource]
       what       who           where
\`\`\`

You grant a role to a member on a resource, and that single sentence is the entire model. Once you can read every permission as who-can-do-what-where, the maze turns into a grammar, and the specifics become details you look up rather than mysteries you fight.

## Service accounts are identities for code

The piece that confuses people coming from elsewhere is the service account. A service account is simply an identity for a non-human, an application or a workload, so your code can be granted permissions the same way a person is.

This is a genuinely good design. Instead of your application carrying around a password or a key, it acts as a service account, and you grant that account exactly the access it needs. The application gets an identity of its own, with its own narrowly scoped permissions, and you can see and control precisely what it is allowed to touch. When you think of a service account as "the user my code logs in as," the whole concept clicks into place.

## The habit that keeps it safe

The most important practice is to avoid downloadable service account keys wherever you possibly can. A key file is a long-lived secret, and long-lived secrets get committed to repos, pasted into messages, and left on laptops. They are the single thing most likely to turn a small mistake into a real incident.

GCP gives you a better path. A workload running on Google Cloud can be attached to a service account directly and receive short-lived credentials automatically, with no key file to leak.

\`\`\`text
Risky:  download a key file and ship it with the app
Safe:   attach the workload to a service account, get short-lived creds automatically
\`\`\`

The day you find yourself about to download a key, treat it as a signal to stop and look for the keyless option instead. It almost always exists, and choosing it removes an entire category of risk for free.

## Roles: prefer narrow over broad

Roles come in broad, convenient bundles and in narrow, specific ones. The broad ones are tempting because they make things work immediately, and that convenience is exactly the trap. An application granted a broad, powerful role can do far more than it needs, which means a compromise of it can too.

The discipline is to grant the narrowest role that lets the workload do its job, scoped to just the resources it actually touches. It is a little more effort to find the right narrow role than to grant a sweeping one, and that effort is the difference between a contained mistake and a wide-open one.

## The whole thing, briefly

- Every permission is: grant a role to a member on a resource. Who, what, where.
- Code gets its own identity through service accounts, like a user for your app.
- Avoid downloadable keys, attach workloads to service accounts for short-lived creds.
- Grant the narrowest role on the smallest scope that does the job.

Hold those few ideas and the part of Google Cloud that feels impenetrable becomes straightforward. It was never really hard. It was just unforgiving of approaching it without a model, and now you have one.
`,
};

export default post;
